import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleXLText from 'shared/components/vxl/typography/primary/title/TitleXLText';
import { isWeb } from 'shared/constants/platform';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import theme from 'shared/styles/theme';

const ELIGIBILITY_CONTENT_TOP_PADDING = 100;

interface ErrorButton {
  buttonText: string;
}

interface CombinedErrorScreen {
  messageHeadline: string;
  messageBody: string;
  primaryButton?: ErrorButton;
  secondaryButton?: ErrorButton;
}

interface TabapayTransferErrorScreenProps {
  errorScreen: CombinedErrorScreen;
  primaryButtonText: string;
  closeButtonHandler: () => void;
  primaryButtonHandler: () => void;
  secondaryButtonHandler?: (button?: ErrorButton) => void | Promise<void>;
  cancelTransferLoading?: boolean;
  variant?: 'standard' | 'eligibility';
  isProcessing?: boolean;
}

const TabapayTransferErrorScreen = ({
  errorScreen,
  primaryButtonText,
  closeButtonHandler,
  primaryButtonHandler,
  secondaryButtonHandler,
  cancelTransferLoading,
  variant = 'standard',
  isProcessing = false,
}: TabapayTransferErrorScreenProps) => {
  const { bottom, top } = useSafeAreaInsets();
  const [pressedButton, setPressedButton] = useState<
    'primary' | 'secondary' | null
  >(null);

  const isPrimaryLoading = isProcessing && pressedButton === 'primary';
  const isSecondaryLoading =
    (cancelTransferLoading || isProcessing) && pressedButton === 'secondary';

  // Shared between close and primary handlers so the partial-auth decision
  // (cancel vs. accept) can fire at most once until isProcessing resets.
  const hasActionFiredRef = useRef(false);

  useEffect(() => {
    if (!isProcessing) {
      hasActionFiredRef.current = false;
      setPressedButton(null);
    }
  }, [isProcessing]);

  const handleClose = useCallback(() => {
    if (hasActionFiredRef.current || isProcessing) {
      return;
    }
    hasActionFiredRef.current = true;
    closeButtonHandler();
  }, [closeButtonHandler, isProcessing]);

  const handlePrimary = useCallback(() => {
    if (hasActionFiredRef.current || isProcessing) {
      return;
    }
    hasActionFiredRef.current = true;
    setPressedButton('primary');
    primaryButtonHandler();
  }, [primaryButtonHandler, isProcessing]);

  const handleSecondary = useCallback(() => {
    if (hasActionFiredRef.current || isProcessing) {
      return;
    }
    hasActionFiredRef.current = true;
    setPressedButton('secondary');
    if (secondaryButtonHandler) {
      void secondaryButtonHandler(errorScreen.secondaryButton);
    }
  }, [secondaryButtonHandler, errorScreen.secondaryButton, isProcessing]);

  if (variant === 'eligibility') {
    return (
      <View style={isWeb ? styles.errorHeaderFixed : styles.errorHeader}>
        {!isWeb ? (
          <NavigationBar
            title={undefined}
            primaryIcon={<Icon iconKey="BACK" iconSize="MD" />}
            primaryOnPress={closeButtonHandler}
            transparent
          />
        ) : null}
        <View style={styles.container}>
          <View style={styles.content}>
            <TitleXLText numberOfLines={0}>
              {errorScreen.messageHeadline}
            </TitleXLText>
            <BodyBaseText numberOfLines={0}>
              {errorScreen.messageBody}
            </BodyBaseText>
          </View>

          <View style={[styles.closeButton, { paddingBottom: bottom }]}>
            <Button
              label={errorScreen.primaryButton?.buttonText || primaryButtonText}
              onPress={handlePrimary}
              size="LARGE"
              type="PRIMARY"
              width="FULL"
              isLoading={isPrimaryLoading}
            />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.errorHeader}>
      <View style={[styles.closeIconWrapper, { top }]}>
        <IconButton
          iconKey="CLOSE"
          iconSize={24}
          iconOnPress={handleClose}
          iconButtonAccessibilityLabel="close"
          isIconDisabled={isPrimaryLoading || isSecondaryLoading}
        />
      </View>
      <ErrorScreen
        screenType={ErrorScreenType.Error}
        messageHeadline={errorScreen.messageHeadline}
        messageBody={errorScreen.messageBody}
        primaryButton={{
          title: errorScreen.primaryButton?.buttonText || primaryButtonText,
          onPress: handlePrimary,
          isLoading: isPrimaryLoading,
        }}
        secondaryButton={
          errorScreen.secondaryButton && secondaryButtonHandler
            ? {
                title: errorScreen.secondaryButton.buttonText,
                onPress: handleSecondary,
                isLoading: isSecondaryLoading,
              }
            : undefined
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  closeButton: {
    backgroundColor: theme.color.background.primary,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-start',
  },
  content: {
    flex: 1,
    gap: theme.spacing.m,
    paddingTop: isWeb ? 0 : ELIGIBILITY_CONTENT_TOP_PADDING,
  },
  errorHeader: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  errorHeaderFixed: {
    // On web, position:absolute with full fill covers the viewport
    // since the header is hidden when the error overlay is visible.
    backgroundColor: theme.color.background.primary,
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 9999,
  },
  closeIconWrapper: {
    left: 4,
    position: 'absolute',
    zIndex: 10,
  },
});

export default TabapayTransferErrorScreen;
